import React from 'react';

import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import Text from '~/components/core/TextComponents/Text';

import ActionStateChip, { StateChip } from '../../ActionStateChip';
import { ACTION_STATE_APPEARANCE, REQUEST_STATE_APPEARANCE, REQUEST_STATE_CHIP } from '../../constants';
import RejectionReason from '../../RejectionReason';
import RequestGestures from '../../RequestGestures';
import type { AskState, ConductorRequest } from '../../types';
import {
  activityCardElementId,
  activityRowKey,
  askRejectionReason,
  requestOutcome,
  requestStateLabel,
} from '../../utils';
import KindCardFrame from '../KindCardFrame';

interface RequestEntryProps {
  notification: StpNotification;
  /** The request this entry is about, where the asks have been read. */
  ask?: ConductorRequest;
  state?: AskState;
  /** Refetches the asks after a gesture, which both panes' request cards resolve from. */
  onAskUpdate: () => Promise<void>;
  /** Outlines the card: the activity panel last took the reader here. */
  isSelected?: boolean;
  /** Shows a card in the activity panel, by its row's key. */
  onSelectActivityCard?: (key: string) => void;
}

interface JoinedRequestEntryProps extends Omit<RequestEntryProps, 'ask' | 'state'> {
  ask: ConductorRequest;
  state: AskState;
}

/**
 * With its ask: Reject while the request waits on the handler. Once it is closed, the outcome line or
 * the note it was rejected with, and nothing to press — Clive closes a request when the work arrives,
 * so Done has no button. The activity panel lists every ask, so the title can show the request's card
 * there.
 */
const JoinedRequestEntry: React.FC<JoinedRequestEntryProps> = ({
  notification,
  ask,
  state,
  onAskUpdate,
  isSelected,
  onSelectActivityCard,
}) => {
  const label = requestStateLabel(state, ask);
  const outcome = requestOutcome(state, ask);
  const cardKey = activityRowKey('request', ask.id);

  return (
    <KindCardFrame
      notification={notification}
      appearance={REQUEST_STATE_APPEARANCE[state]}
      kind="request"
      title={ask.ask}
      subtitle={ask.summary}
      chip={label ? <StateChip label={label} className={REQUEST_STATE_CHIP[state]} /> : null}
      isSelected={isSelected}
      onSelect={onSelectActivityCard ? () => onSelectActivityCard(cardKey) : undefined}
      controls={activityCardElementId(cardKey)}
    >
      {outcome ? (
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {outcome}
        </Text>
      ) : null}
      <RejectionReason reason={askRejectionReason(state, ask)} />
      <RequestGestures request={ask} state={state} onAskUpdate={onAskUpdate} />
    </KindCardFrame>
  );
};

/**
 * An entry about a request, drawn from the ask rather than the entry, which is a snapshot from when
 * it was published. The same icon in every state: what a request is does not change when it closes.
 *
 * Before the asks are read, nothing has established where the request is, so the card says what the
 * entry said and claims no state, the way an action entry with no row does.
 */
const RequestEntry: React.FC<RequestEntryProps> = ({
  notification,
  ask,
  state,
  onAskUpdate,
  isSelected,
  onSelectActivityCard,
}) =>
  ask && state ? (
    <JoinedRequestEntry
      notification={notification}
      ask={ask}
      state={state}
      onAskUpdate={onAskUpdate}
      isSelected={isSelected}
      onSelectActivityCard={onSelectActivityCard}
    />
  ) : (
    <KindCardFrame
      notification={notification}
      appearance={ACTION_STATE_APPEARANCE.unresolved}
      kind="request"
      title={notification.title}
      subtitle={notification.metadata?.summary}
      chip={<ActionStateChip state="unresolved" />}
      isSelected={isSelected}
    />
  );

export default RequestEntry;
